/**
 * Structured Error System
 *
 * Provides clear error classification into three categories:
 * - USER: Invalid input, missing permissions, resource not found (user can fix)
 * - DATA: Invalid data state, constraint violations, missing relations (data issue)
 * - SYSTEM: Network failures, server errors, timeouts (infrastructure issue)
 *
 * Usage:
 *   import { NexusError, ErrorCategory, createError } from '@/lib/errors';
 *
 *   // Throw typed errors:
 *   throw createError.user('validation', 'Email is required');
 *   throw createError.data('constraint', 'Donor not found', { donorId });
 *   throw createError.system('network', 'Failed to connect');
 *
 *   // Check error types:
 *   if (isUserError(error)) { showToast(error.userMessage); }
 */

import { traceContext } from "./traceContext";

// ============================================================================
// Error Categories
// ============================================================================

/**
 * High-level error categories
 *
 * USER: Problems the user can fix (bad input, missing permissions)
 * DATA: Problems with data state (missing records, invalid state)
 * SYSTEM: Infrastructure problems (network, server, timeout)
 */
export type ErrorCategory = "user" | "data" | "system";

/**
 * Specific error codes within each category
 */
export type UserErrorCode =
  | "validation" // Invalid input format or value
  | "authentication" // Not logged in
  | "authorization" // Not permitted
  | "not_found" // Resource doesn't exist (user-facing)
  | "rate_limit" // Too many requests
  | "conflict"; // Resource already exists or conflict

export type DataErrorCode =
  | "not_found" // Record doesn't exist (data layer)
  | "constraint" // Foreign key, unique constraint violation
  | "invalid_state" // Data in unexpected state
  | "stale" // Optimistic locking failure
  | "integrity"; // Data integrity issue

export type SystemErrorCode =
  | "network" // Network connectivity issue
  | "timeout" // Request timed out
  | "server" // 5xx server error
  | "unavailable" // Service unavailable
  | "internal" // Unexpected internal error
  | "dependency"; // External dependency failure (AI API, etc.)

export type ErrorCode = UserErrorCode | DataErrorCode | SystemErrorCode;

// ============================================================================
// NexusError Class
// ============================================================================

export interface NexusErrorOptions {
  /** The underlying cause of this error */
  cause?: unknown;
  /** Additional context data */
  context?: Record<string, unknown>;
  /** HTTP status code if applicable */
  statusCode?: number;
  /** Whether this error can be retried */
  retryable?: boolean;
  /** Custom user-facing message */
  userMessage?: string;
}

/**
 * Structured error class for Nexus
 */
export class NexusError extends Error {
  readonly category: ErrorCategory;
  readonly code: ErrorCode;
  readonly traceId: string;
  readonly timestamp: number;
  readonly context: Record<string, unknown>;
  readonly statusCode?: number;
  readonly retryable: boolean;
  readonly userMessage: string;

  constructor(
    category: ErrorCategory,
    code: ErrorCode,
    message: string,
    options: NexusErrorOptions = {},
  ) {
    super(message);
    this.name = "NexusError";
    this.category = category;
    this.code = code;
    this.traceId = traceContext.getTraceId();
    this.timestamp = Date.now();
    this.context = options.context ?? {};
    this.statusCode = options.statusCode;
    this.retryable = options.retryable ?? this.defaultRetryable();
    this.userMessage = options.userMessage ?? this.defaultUserMessage();

    // Capture the cause
    if (options.cause) {
      this.cause = options.cause;
    }

    // Maintain stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NexusError);
    }
  }

  private defaultRetryable(): boolean {
    // System errors are generally retryable
    if (this.category === "system") {
      return this.code !== "internal";
    }
    // Rate limit is retryable
    if (this.code === "rate_limit") {
      return true;
    }
    // Stale data can be retried after refresh
    if (this.code === "stale") {
      return true;
    }
    return false;
  }

  private defaultUserMessage(): string {
    // Category-level defaults
    const categoryMessages: Record<ErrorCategory, string> = {
      user: "Please check your input and try again.",
      data: "There was a problem with the data. Please refresh and try again.",
      system: "Something went wrong. Please try again in a moment.",
    };

    // Code-specific messages
    const codeMessages: Partial<Record<ErrorCode, string>> = {
      validation: "Please check your input and try again.",
      authentication: "Please sign in to continue.",
      authorization: "You don't have permission to do this.",
      not_found: "The requested item was not found.",
      rate_limit: "Too many requests. Please wait a moment.",
      conflict: "This item already exists or conflicts with another.",
      constraint: "This operation cannot be completed due to data constraints.",
      invalid_state: "The data is in an unexpected state. Please refresh.",
      stale: "The data has changed. Please refresh and try again.",
      network: "Unable to connect. Please check your internet connection.",
      timeout: "The request took too long. Please try again.",
      server: "Our servers are having trouble. Please try again later.",
      unavailable: "This service is temporarily unavailable.",
      internal: "An unexpected error occurred.",
      dependency: "An external service is unavailable. Please try again.",
    };

    return codeMessages[this.code] ?? categoryMessages[this.category];
  }

  /**
   * Convert to a plain object for serialization
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      category: this.category,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      traceId: this.traceId,
      timestamp: this.timestamp,
      context: this.context,
      statusCode: this.statusCode,
      retryable: this.retryable,
      stack: this.stack,
    };
  }

  /**
   * Create a log-safe representation (no sensitive data)
   */
  toLogSafe(): Record<string, unknown> {
    return {
      name: this.name,
      category: this.category,
      code: this.code,
      message: this.message,
      traceId: this.traceId,
      timestamp: this.timestamp,
      statusCode: this.statusCode,
      retryable: this.retryable,
      // Exclude context which might contain sensitive data
    };
  }
}

// ============================================================================
// Error Factory
// ============================================================================

/**
 * Factory for creating typed errors
 */
export const createError = {
  /**
   * Create a user error (input, permissions, not found)
   */
  user(
    code: UserErrorCode,
    message: string,
    options?: NexusErrorOptions,
  ): NexusError {
    const statusCodes: Record<UserErrorCode, number> = {
      validation: 400,
      authentication: 401,
      authorization: 403,
      not_found: 404,
      rate_limit: 429,
      conflict: 409,
    };
    return new NexusError("user", code, message, {
      ...options,
      statusCode: options?.statusCode ?? statusCodes[code],
    });
  },

  /**
   * Create a data error (constraints, invalid state)
   */
  data(
    code: DataErrorCode,
    message: string,
    options?: NexusErrorOptions,
  ): NexusError {
    return new NexusError("data", code, message, {
      ...options,
      statusCode: options?.statusCode ?? 422,
    });
  },

  /**
   * Create a system error (network, server, timeout)
   */
  system(
    code: SystemErrorCode,
    message: string,
    options?: NexusErrorOptions,
  ): NexusError {
    const statusCodes: Record<SystemErrorCode, number> = {
      network: 0,
      timeout: 408,
      server: 500,
      unavailable: 503,
      internal: 500,
      dependency: 502,
    };
    return new NexusError("system", code, message, {
      ...options,
      statusCode: options?.statusCode ?? statusCodes[code],
    });
  },

  /**
   * Wrap an unknown error as a NexusError
   */
  wrap(error: unknown, category?: ErrorCategory): NexusError {
    if (error instanceof NexusError) {
      return error;
    }

    // Try to classify the error
    const classified = classifyUnknownError(error);
    const effectiveCategory = category ?? classified.category;
    const code = classified.code;

    const message = error instanceof Error ? error.message : String(error);

    return new NexusError(effectiveCategory, code, message, {
      cause: error,
      statusCode: classified.statusCode,
    });
  },
};

// ============================================================================
// Error Classification
// ============================================================================

interface ClassifiedError {
  category: ErrorCategory;
  code: ErrorCode;
  statusCode?: number;
}

/**
 * Classify an unknown error into our error system
 */
function classifyUnknownError(error: unknown): ClassifiedError {
  // Network errors
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return { category: "system", code: "network" };
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();

    // Network patterns
    if (
      msg.includes("network") ||
      msg.includes("offline") ||
      msg.includes("failed to fetch")
    ) {
      return { category: "system", code: "network" };
    }

    // Timeout patterns
    if (
      msg.includes("timeout") ||
      msg.includes("timed out") ||
      msg.includes("aborted")
    ) {
      return { category: "system", code: "timeout" };
    }

    // Auth patterns
    if (
      msg.includes("unauthorized") ||
      msg.includes("jwt") ||
      msg.includes("token expired")
    ) {
      return { category: "user", code: "authentication", statusCode: 401 };
    }

    if (msg.includes("forbidden") || msg.includes("permission")) {
      return { category: "user", code: "authorization", statusCode: 403 };
    }

    // Not found patterns
    if (msg.includes("not found") || msg.includes("does not exist")) {
      return { category: "user", code: "not_found", statusCode: 404 };
    }

    // Validation patterns
    if (
      msg.includes("invalid") ||
      msg.includes("validation") ||
      msg.includes("required")
    ) {
      return { category: "user", code: "validation", statusCode: 400 };
    }

    // Constraint patterns
    if (
      msg.includes("constraint") ||
      msg.includes("duplicate") ||
      msg.includes("unique")
    ) {
      return { category: "data", code: "constraint", statusCode: 409 };
    }
  }

  // Check for status codes in error objects
  if (typeof error === "object" && error !== null) {
    const statusCode = extractStatusCode(error);
    if (statusCode) {
      if (statusCode === 400)
        return { category: "user", code: "validation", statusCode };
      if (statusCode === 401)
        return { category: "user", code: "authentication", statusCode };
      if (statusCode === 403)
        return { category: "user", code: "authorization", statusCode };
      if (statusCode === 404)
        return { category: "user", code: "not_found", statusCode };
      if (statusCode === 409)
        return { category: "data", code: "conflict", statusCode };
      if (statusCode === 422)
        return { category: "data", code: "constraint", statusCode };
      if (statusCode === 429)
        return { category: "user", code: "rate_limit", statusCode };
      if (statusCode >= 500)
        return { category: "system", code: "server", statusCode };
    }
  }

  // Default to system internal error
  return { category: "system", code: "internal" };
}

function extractStatusCode(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;

  const obj = error as Record<string, unknown>;

  if (typeof obj.status === "number") return obj.status;
  if (typeof obj.statusCode === "number") return obj.statusCode;

  if (obj.response && typeof obj.response === "object") {
    const response = obj.response as Record<string, unknown>;
    if (typeof response.status === "number") return response.status;
  }

  return undefined;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isNexusError(error: unknown): error is NexusError {
  return error instanceof NexusError;
}

export function isUserError(error: unknown): error is NexusError {
  return isNexusError(error) && error.category === "user";
}

export function isDataError(error: unknown): error is NexusError {
  return isNexusError(error) && error.category === "data";
}

export function isSystemError(error: unknown): error is NexusError {
  return isNexusError(error) && error.category === "system";
}

export function isRetryableError(error: unknown): boolean {
  if (isNexusError(error)) {
    return error.retryable;
  }
  // For unknown errors, check if they look retryable
  const classified = classifyUnknownError(error);
  return classified.category === "system" && classified.code !== "internal";
}

// ============================================================================
// Error Utilities
// ============================================================================

/**
 * Get a user-friendly message from any error
 */
export function getUserMessage(error: unknown): string {
  if (isNexusError(error)) {
    return error.userMessage;
  }
  if (error instanceof Error) {
    // Don't expose raw error messages to users
    return "An unexpected error occurred. Please try again.";
  }
  return "Something went wrong. Please try again.";
}

/**
 * Get the trace ID from any error
 */
export function getErrorTraceId(error: unknown): string {
  if (isNexusError(error)) {
    return error.traceId;
  }
  return traceContext.getTraceId();
}

/**
 * Extract error details for logging
 */
export function getErrorDetails(error: unknown): Record<string, unknown> {
  if (isNexusError(error)) {
    return error.toLogSafe();
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      traceId: traceContext.getTraceId(),
    };
  }

  return {
    error: String(error),
    traceId: traceContext.getTraceId(),
  };
}
